import { forwardRef } from "react";
import { cn } from "@utils/index";
import type { ButtonProps } from "./Button.types";

/**
 * Primary UI Button component
 *
 * @example
 * <Button variant="primary" size="md">Click me</Button>
 * <Button variant="secondary" size="lg" rightIcon={<ArrowIcon />}>Learn More</Button>
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      isFullWidth = false,
      isLoading = false,
      isDisabled = false,
      leftIcon,
      rightIcon,
      className,
      type = "button",
      ...props
    },
    ref
  ) => {
    const baseStyles =
      "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

    const variants = {
      primary:
        "bg-[var(--color-tomato-200)] text-white hover:bg-[var(--color-tomato-100)] focus-visible:ring-[var(--color-tomato-200)]",
      secondary:
        "bg-[var(--color-whitesmoke-200)] text-[var(--color-gray-300)] hover:bg-[var(--color-gainsboro-300)] focus-visible:ring-[var(--color-gray-300)]",
      outline:
        "border border-[var(--color-gis-dark-blue)] text-[var(--color-gis-dark-blue)] hover:bg-[var(--color-gis-dark-blue)] hover:text-white focus-visible:ring-[var(--color-gis-dark-blue)]",
      ghost:
        "text-[var(--color-gis-dark-blue)] hover:bg-[var(--color-whitesmoke-100)] focus-visible:ring-[var(--color-gis-dark-blue)]",
      dark: "bg-[var(--color-gray-400)] text-white hover:bg-[var(--color-darkslategray)] focus-visible:ring-[var(--color-gray-400)]",
      white:
        "bg-white text-[var(--color-gis-black)] hover:bg-[var(--color-gainsboro-100)] focus-visible:ring-[var(--color-gis-black)]",
    };

    const sizes = {
      sm: "h-9 px-4 text-sm rounded-[var(--radius-3xl)]",
      md: "h-11 px-6 text-base rounded-[var(--radius-3xl)]",
      lg: "h-14 px-8 text-lg rounded-[var(--radius-3xl)]",
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled || isLoading}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          isFullWidth && "w-full",
          className
        )}
        {...props}
      >
        {isLoading ? (
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : leftIcon ? (
          <span className="mr-2">{leftIcon}</span>
        ) : null}
        {children}
        {rightIcon && !isLoading && <span className="ml-2">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button };
